// OUTGOING CALLS — the administrator's side (Stage 4.8b, guide §6.12). Businesses ask for outgoing calls; the administrator
// approves (local, international and its countries), declines or revokes (D8), sets a business its own price a minute per
// destination where the standard one should not apply (D9), keeps the carriers — one row per SIP trunk on the phone system, the way
// each wants the number written and the countries it serves; the cheapest carrier carries a call (D11) — and can ask, for any
// business and number, exactly what outboundCheck answers: allowed or why not, the carriers in order, the number shown, the hold.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import { AlertCircle, CheckCircle2, PhoneOutgoing, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";

import axiosInstance from "../../../services/axiosInstance";
import { countryName, DIAL_FORMATS } from "../../pages/outbound/outboundMeta";

const ui = { fontFamily: "var(--font-ui)" };
const TONE = {
  none: { fg: "var(--ink-600)", bg: "var(--surface-2)" },
  requested: { fg: "var(--warning)", bg: "var(--warning-bg)" },
  approved: { fg: "var(--success)", bg: "var(--success-bg)" },
  declined: { fg: "var(--danger)", bg: "var(--danger-bg)" },
  revoked: { fg: "var(--danger)", bg: "var(--danger-bg)" },
};
const TH = { ...ui, fontWeight: 700, fontSize: "var(--t-caption)", color: "var(--ink-500)", whiteSpace: "nowrap" };
const TD = { ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-900)" };
const usd = (n) => (Number.isFinite(Number(n)) ? `$${Number(n).toFixed(4)}` : "—");

function StatusChip({ status }) {
  const { t } = useTranslation("outbound");
  const c = TONE[status] ?? TONE.none;
  return <Chip size="small" label={t(`status.${status}`)} sx={{ ...ui, height: 22, fontWeight: 700, fontSize: "var(--t-caption)", color: c.fg, bgcolor: c.bg }} />;
}

function Countries({ codes, value, onChange, multiple = false, label, exclude = null }) {
  const { i18n } = useTranslation("outbound");
  const lang = i18n.language;
  const options = useMemo(() => codes.filter((c) => c !== exclude).sort((a, b) => countryName(a, lang).localeCompare(countryName(b, lang), lang)), [codes, exclude, lang]);
  return (
    <Autocomplete multiple={multiple} size="small" options={options} value={value ?? (multiple ? [] : null)} onChange={(_, v) => onChange(v)}
      getOptionLabel={(c) => `${countryName(c, lang)} (${c})`} filterSelectedOptions={multiple}
      renderInput={(p) => <TextField {...p} label={label} />} />
  );
}

const scopeText = (t, lang, x, home) => {
  if (!x) return "—";
  const parts = [];
  if (x.local) parts.push(t("admin.localOf", { country: countryName(home, lang) || home || "?" }));
  if (x.international) parts.push((x.countries ?? []).join(", ") || t("admin.intl"));
  return parts.join(" · ") || "—";
};

// ── One business: the decision, its own prices, and the check ──
function BusinessDialog({ userId, onClose, onChanged }) {
  const { t, i18n } = useTranslation("outbound");
  const lang = i18n.language;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null);
  const [prices, setPrices] = useState([]);
  const [check, setCheck] = useState({ to: "", purpose: "ai", res: null });

  const take = useCallback((data) => {
    setD(data);
    const a = data.access;
    const src = a.status === "requested" || a.changeRequested ? (a.request ?? {}) : (a.grant ?? a.request ?? {});
    setForm({ homeCountry: a.homeCountry, local: !!src.local, international: !!src.international, countries: src.countries ?? [], note: "" });
    setPrices(data.overrides ?? []);
  }, []);
  useEffect(() => {
    axiosInstance.get(`/admin/outbound/access/${userId}`).then(({ data }) => take(data)).catch(() => setErr("load"));
  }, [userId, take]);

  const decide = async (action) => {
    setBusy(action); setErr(null);
    try {
      const { data } = await axiosInstance.post(`/admin/outbound/access/${userId}/decide`, { action, ...form });
      take({ ...d, ...data }); onChanged();
    } catch (e) { setErr((e.response?.data?.fields ?? ["failed"]).join(", ")); }
    finally { setBusy(null); }
  };
  const savePrices = async () => {
    setBusy("prices"); setErr(null);
    try {
      const { data } = await axiosInstance.put(`/admin/outbound/access/${userId}/prices`, { prices: prices.map((p) => ({ ...p, perMinUsd: Number(p.perMinUsd) })) });
      take({ ...d, ...data });
    } catch (e) { setErr((e.response?.data?.fields ?? ["failed"]).join(", ")); }
    finally { setBusy(null); }
  };
  const runCheck = async () => {
    setBusy("check");
    try { const { data } = await axiosInstance.get("/admin/outbound/check", { params: { userId, to: check.to, purpose: check.purpose } }); setCheck((c) => ({ ...c, res: data })); }
    catch { setCheck((c) => ({ ...c, res: { ok: false, reason: "failed" } })); }
    finally { setBusy(null); }
  };

  const a = d?.access;
  const standard = (country, type) => d?.prices?.find((p) => p.country === country)?.[type === "*" ? "mobile" : type]?.perMinUsd;
  const priceCountries = useMemo(() => [...new Set([form?.homeCountry, ...(form?.countries ?? []), ...prices.map((p) => p.country)].filter(Boolean))], [form, prices]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="ob-dialog-title">
      <DialogTitle id="ob-dialog-title" sx={{ ...ui, display: "flex", alignItems: "center", gap: "var(--sp-2)" }}>
        <PhoneOutgoing size={18} aria-hidden="true" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...ui, fontWeight: 800, fontSize: "var(--t-h4)" }}>{d?.user?.name || d?.user?.email || `#${userId}`}</Typography>
          {d?.user?.email && <Typography sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-500)" }}>{d.user.email}</Typography>}
        </Box>
        {a && <StatusChip status={a.status} />}
        <IconButton onClick={onClose} aria-label={t("admin.close")}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: "var(--sp-5)" }}>
        {!d && !err && <Box sx={{ display: "flex", justifyContent: "center", py: "var(--sp-6)" }}><CircularProgress size={22} /></Box>}
        {err && <Typography role="alert" sx={{ ...ui, color: "var(--danger)", fontSize: "var(--t-body-sm)" }}>{err === "load" ? t("loadFailed") : t("admin.invalid", { fields: err })}</Typography>}
        {a && form && (
          <>
            <Box sx={{ display: "grid", gap: "var(--sp-1)" }}>
              <Typography sx={{ ...ui, fontWeight: 700, fontSize: "var(--t-body-sm)" }}>{t("admin.asked")}</Typography>
              <Typography sx={TD}>{scopeText(t, lang, a.request, a.homeCountry)}</Typography>
              {a.request?.note && <Typography sx={{ ...TD, color: "var(--ink-600)" }}>“{a.request.note}”</Typography>}
              {a.status === "approved" && <Typography sx={{ ...TD, color: "var(--ink-600)" }}>{t("admin.grantedNow", { scope: scopeText(t, lang, a.grant, a.homeCountry) })}</Typography>}
              {a.changeRequested && <Chip size="small" label={t("admin.changeRequested")} sx={{ justifySelf: "start", color: "var(--warning)", bgcolor: "var(--warning-bg)", fontWeight: 700 }} />}
            </Box>

            <Box sx={{ display: "grid", gap: "var(--sp-3)" }}>
              <Typography sx={{ ...ui, fontWeight: 700, fontSize: "var(--t-body-sm)" }}>{t("admin.approveTitle")}</Typography>
              <Countries codes={d.countries} value={form.homeCountry} onChange={(v) => setForm({ ...form, homeCountry: v })} label={t("form.home")} />
              <Box sx={{ display: "flex", gap: "var(--sp-4)", flexWrap: "wrap" }}>
                <FormControlLabel control={<Checkbox checked={form.local} onChange={(e) => setForm({ ...form, local: e.target.checked })} />} label={t("admin.local")} />
                <FormControlLabel control={<Checkbox checked={form.international} onChange={(e) => setForm({ ...form, international: e.target.checked })} />} label={t("admin.intl")} />
              </Box>
              {form.international && <Countries codes={d.countries} value={form.countries} multiple exclude={form.homeCountry} onChange={(v) => setForm({ ...form, countries: v })} label={t("form.countries")} />}
              <TextField size="small" label={t("admin.note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} inputProps={{ maxLength: 500 }} />
              <Box sx={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                <Button variant="contained" disableElevation disabled={!!busy} onClick={() => decide("approve")} sx={{ ...ui, minHeight: 44, fontWeight: 700 }}>
                  {busy === "approve" ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : t("admin.approve")}
                </Button>
                {(a.status === "requested" || a.changeRequested) && (
                  <Button variant="outlined" disabled={!!busy} onClick={() => decide("decline")} sx={{ ...ui, minHeight: 44 }}>{t("admin.decline")}</Button>
                )}
                {a.status === "approved" && (
                  <Button variant="outlined" disabled={!!busy} onClick={() => decide("revoke")} sx={{ ...ui, minHeight: 44, color: "var(--danger)", borderColor: "var(--danger)" }}>{t("admin.revoke")}</Button>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "grid", gap: "var(--sp-2)" }}>
              <Typography sx={{ ...ui, fontWeight: 700, fontSize: "var(--t-body-sm)" }}>{t("admin.pricesTitle")}</Typography>
              <Typography sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-500)" }}>{t("admin.pricesHelp")}</Typography>
              {prices.map((p, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "minmax(140px,1fr) 130px 130px auto", gap: "var(--sp-2)", alignItems: "center" }}>
                  <TextField select size="small" label={t("col.country")} value={p.country} onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, country: e.target.value } : x)))}>
                    {priceCountries.map((c) => <MenuItem key={c} value={c}>{countryName(c, lang)} ({c})</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label={t("admin.type")} value={p.numberType} onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, numberType: e.target.value } : x)))}>
                    {["mobile", "landline", "*"].map((k) => <MenuItem key={k} value={k}>{t(`admin.type_${k === "*" ? "any" : k}`)}</MenuItem>)}
                  </TextField>
                  <TextField size="small" type="number" label={t("admin.usdMin")} value={p.perMinUsd} inputProps={{ step: "0.0001", min: 0, dir: "ltr" }}
                    helperText={t("admin.standardIs", { usd: usd(standard(p.country, p.numberType)) })}
                    onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, perMinUsd: e.target.value } : x)))} />
                  <IconButton aria-label={t("admin.removePrice")} onClick={() => setPrices(prices.filter((_, j) => j !== i))}><Trash2 size={16} /></IconButton>
                </Box>
              ))}
              <Box sx={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                <Button startIcon={<Plus size={16} />} disabled={!priceCountries.length} onClick={() => setPrices([...prices, { country: priceCountries[0], numberType: "*", perMinUsd: "" }])} sx={{ ...ui, minHeight: 44 }}>
                  {t("admin.addPrice")}
                </Button>
                <Button variant="outlined" disabled={!!busy} onClick={savePrices} sx={{ ...ui, minHeight: 44 }}>
                  {busy === "prices" ? <CircularProgress size={16} /> : t("admin.savePrices")}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gap: "var(--sp-2)" }}>
              <Typography sx={{ ...ui, fontWeight: 700, fontSize: "var(--t-body-sm)" }}>{t("admin.checkTitle")}</Typography>
              <Box component="form" onSubmit={(e) => { e.preventDefault(); runCheck(); }} sx={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                <TextField size="small" label={t("try.label")} value={check.to} onChange={(e) => setCheck({ ...check, to: e.target.value })} inputProps={{ dir: "ltr" }} sx={{ flex: "1 1 200px" }} />
                <TextField select size="small" label={t("admin.purpose")} value={check.purpose} onChange={(e) => setCheck({ ...check, purpose: e.target.value })} sx={{ width: 150 }}>
                  <MenuItem value="ai">{t("admin.purpose_ai")}</MenuItem>
                  <MenuItem value="staff">{t("admin.purpose_staff")}</MenuItem>
                </TextField>
                <Button type="submit" variant="outlined" disabled={!check.to.trim() || busy === "check"} startIcon={<Search size={16} />} sx={{ ...ui, minHeight: 44 }}>{t("try.go")}</Button>
              </Box>
              {check.res && <CheckResult r={check.res} />}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ ...ui, minHeight: 44 }}>{t("admin.close")}</Button></DialogActions>
    </Dialog>
  );
}

function CheckResult({ r }) {
  const { t } = useTranslation("outbound");
  if (!r.ok) {
    return (
      <Box role="status" sx={{ display: "grid", gap: "var(--sp-1)" }}>
        <Box sx={{ display: "flex", gap: "var(--sp-2)", color: "var(--danger)" }}>
          <AlertCircle size={16} aria-hidden="true" />
          <Typography sx={{ ...TD, color: "inherit" }}>{t(`reason.${r.reason}`, { defaultValue: r.reason })}</Typography>
        </Box>
        {(r.skipped ?? []).map((s) => <Typography key={s.endpoint} sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-500)" }}>{s.carrier}: {t(`admin.skip_${s.why}`)}</Typography>)}
      </Box>
    );
  }
  return (
    <Box role="status" sx={{ display: "grid", gap: "var(--sp-1)" }}>
      <Box sx={{ display: "flex", gap: "var(--sp-2)", color: "var(--success)" }}>
        <CheckCircle2 size={16} aria-hidden="true" />
        <Typography sx={{ ...TD, color: "inherit" }}>{t("admin.checkOk", { hold: Math.round(r.holdTokens), minutes: r.minutes, perMin: Math.round(r.perMinTokens) })}</Typography>
      </Box>
      {r.routes.map((x, i) => (
        <Typography key={x.trunkId} sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-600)" }}>
          {i + 1}. {x.carrier} — <span dir="ltr">{x.dial}</span> · {t("admin.shows")} <span dir="ltr">{x.callerId}</span> · {t("admin.cost")} {usd(x.price.carrierPerMinUsd)} · {t("admin.price")} {usd(x.price.perMinUsd)} ({t(`admin.src_${x.price.source}`)})
        </Typography>
      ))}
      {(r.skipped ?? []).map((s) => <Typography key={s.endpoint} sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-400)" }}>{s.carrier}: {t(`admin.skip_${s.why}`)}</Typography>)}
    </Box>
  );
}

// ── The carriers ──
function TrunkDialog({ trunk, countries, onClose, onSaved }) {
  const { t } = useTranslation("outbound");
  const [f, setF] = useState(() => ({
    carrier: trunk?.carrier ?? "", endpoint: trunk?.endpoint ?? "", dialFormat: trunk?.dialFormat ?? "plus",
    all: !trunk || trunk.countries === "*", countries: trunk && trunk.countries !== "*" ? trunk.countries.split(",") : [], label: trunk?.label ?? "",
  }));
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setErr(null);
    const body = { carrier: f.carrier, endpoint: f.endpoint, dialFormat: f.dialFormat, countries: f.all ? "*" : f.countries, label: f.label };
    try {
      const { data } = trunk ? await axiosInstance.patch(`/admin/outbound/trunks/${trunk.id}`, body) : await axiosInstance.post("/admin/outbound/trunks", body);
      onSaved(data.trunks);
    } catch (e) { setErr((e.response?.data?.fields ?? ["failed"]).join(", ")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="ob-trunk-title">
      <DialogTitle id="ob-trunk-title" sx={ui}>{t(trunk ? "admin.editCarrier" : "admin.addCarrier")}</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: "var(--sp-3)" }}>
        <TextField size="small" label={t("admin.carrier")} value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} helperText={t("admin.carrierHelp")} inputProps={{ dir: "ltr" }} />
        <TextField size="small" label={t("admin.endpoint")} value={f.endpoint} onChange={(e) => setF({ ...f, endpoint: e.target.value })} helperText={t("admin.endpointHelp")} inputProps={{ dir: "ltr" }} />
        <TextField select size="small" label={t("admin.dialFormat")} value={f.dialFormat} onChange={(e) => setF({ ...f, dialFormat: e.target.value })}>
          {DIAL_FORMATS.map((k) => <MenuItem key={k} value={k}>{t(`admin.format_${k === "00" ? "zz" : k}`)}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Checkbox checked={f.all} onChange={(e) => setF({ ...f, all: e.target.checked })} />} label={t("admin.allCountries")} />
        {!f.all && <Countries codes={countries} value={f.countries} multiple onChange={(v) => setF({ ...f, countries: v })} label={t("admin.servesCountries")} />}
        <TextField size="small" label={t("admin.label")} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        {err && <Typography role="alert" sx={{ ...ui, color: "var(--danger)", fontSize: "var(--t-body-sm)" }}>{t("admin.invalid", { fields: err })}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ ...ui, minHeight: 44 }}>{t("cancel")}</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy} sx={{ ...ui, minHeight: 44, fontWeight: 700, bgcolor: "var(--brand-violet)" }}>{t("admin.save")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function Carriers() {
  const { t } = useTranslation("outbound");
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => axiosInstance.get("/admin/outbound/trunks").then(({ data }) => setD(data)).catch(() => setD({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (tr) => {
    const { data } = await axiosInstance.patch(`/admin/outbound/trunks/${tr.id}`, { active: !tr.active }).catch(() => ({ data: null }));
    if (data) setD((x) => ({ ...x, trunks: data.trunks }));
  };
  if (!d) return <Box sx={{ display: "flex", justifyContent: "center", py: "var(--sp-6)" }}><CircularProgress size={22} /></Box>;
  if (d.error) return <Typography role="alert" sx={{ ...TD, color: "var(--danger)" }}>{t("loadFailed")}</Typography>;
  return (
    <Box sx={{ display: "grid", gap: "var(--sp-3)" }}>
      <Typography sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-600)" }}>{t("admin.carriersHelp")}</Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>
            {["carrier", "endpoint", "dialFormat", "countries", "active", ""].map((k) => <TableCell key={k} sx={TH}>{k ? t(`admin.${k}`) : ""}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {d.trunks.map((tr) => (
              <TableRow key={tr.id} hover>
                <TableCell sx={TD}><b>{tr.carrier}</b>{tr.label ? <Typography component="span" sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-500)", marginInlineStart: "var(--sp-2)" }}>{tr.label}</Typography> : null}</TableCell>
                <TableCell sx={{ ...TD, fontFamily: "var(--font-mono)" }} dir="ltr">{tr.endpoint}</TableCell>
                <TableCell sx={TD}>{t(`admin.format_${tr.dialFormat === "00" ? "zz" : tr.dialFormat}`)}</TableCell>
                <TableCell sx={TD}>{tr.countries === "*" ? t("admin.allCountries") : tr.countries.split(",").join(", ")}</TableCell>
                <TableCell><Switch checked={tr.active} onChange={() => toggle(tr)} inputProps={{ "aria-label": t("admin.active") }} /></TableCell>
                <TableCell><Button size="small" onClick={() => setEdit(tr)} sx={ui}>{t("admin.edit")}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box><Button startIcon={<Plus size={16} />} variant="outlined" onClick={() => setEdit({})} sx={{ ...ui, minHeight: 44 }}>{t("admin.addCarrier")}</Button></Box>
      {edit && <TrunkDialog trunk={edit.id ? edit : null} countries={d.countries} onClose={() => setEdit(null)} onSaved={(trunks) => { setD((x) => ({ ...x, trunks })); setEdit(null); }} />}
    </Box>
  );
}

// ── The page ──
export default function OutboundAdmin() {
  const { t, i18n } = useTranslation("outbound");
  const lang = i18n.language;
  const [tab, setTab] = useState("requests");
  const [status, setStatus] = useState("");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    setD(null);
    axiosInstance.get("/admin/outbound/access", { params: status ? { status } : {} }).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ display: "grid", gap: "var(--sp-4)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)" }}>
        <PhoneOutgoing size={22} aria-hidden="true" color="var(--brand-violet)" />
        <Box sx={{ flex: 1 }}>
          <Typography component="h1" sx={{ ...ui, fontWeight: 800, fontSize: "var(--t-h3)", color: "var(--ink-900)" }}>{t("admin.title")}</Typography>
          <Typography sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-500)" }}>{t("admin.subtitle")}</Typography>
        </Box>
        {d?.waiting > 0 && <Chip label={t("admin.waiting", { n: d.waiting })} sx={{ fontWeight: 700, color: "var(--warning)", bgcolor: "var(--warning-bg)" }} />}
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label={t("admin.title")}>
        <Tab value="requests" label={t("admin.tabRequests")} sx={ui} />
        <Tab value="carriers" label={t("admin.tabCarriers")} sx={ui} />
      </Tabs>
      {tab === "carriers" ? <Carriers /> : (
        <Box sx={{ display: "grid", gap: "var(--sp-3)" }}>
          <Box sx={{ display: "flex", gap: "var(--sp-2)", alignItems: "center" }}>
            <TextField select size="small" label={t("admin.filter")} value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 220 }}
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t("admin.all")}</MenuItem>
              {["requested", "approved", "declined", "revoked"].map((s) => <MenuItem key={s} value={s}>{t(`status.${s}`)}</MenuItem>)}
            </TextField>
            <IconButton onClick={load} aria-label={t("admin.refresh")}><RefreshCw size={16} /></IconButton>
          </Box>
          {!d ? <Box sx={{ display: "flex", justifyContent: "center", py: "var(--sp-6)" }}><CircularProgress size={22} /></Box>
            : d.error ? <Typography role="alert" sx={{ ...TD, color: "var(--danger)" }}>{t("loadFailed")}</Typography>
              : !d.rows.length ? <Typography sx={{ ...TD, color: "var(--ink-500)" }}>{t("admin.none")}</Typography> : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      {["business", "status", "asked", "granted", "switches", "when"].map((k) => <TableCell key={k} sx={TH}>{t(`admin.col_${k}`)}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {d.rows.map((r) => (
                        <TableRow key={r.userId} hover onClick={() => setOpen(r.userId)} sx={{ cursor: "pointer" }} tabIndex={0}
                          onKeyDown={(e) => { if (e.key === "Enter") setOpen(r.userId); }}>
                          <TableCell sx={TD}>
                            <b>{r.userName || `#${r.userId}`}</b>
                            <Typography sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-500)" }}>{r.userEmail}</Typography>
                          </TableCell>
                          <TableCell sx={TD}>
                            <Box sx={{ display: "flex", gap: "var(--sp-1)", flexWrap: "wrap" }}>
                              <StatusChip status={r.status} />
                              {r.changeRequested && <Chip size="small" label={t("admin.changeRequested")} sx={{ height: 22, color: "var(--warning)", bgcolor: "var(--warning-bg)", fontWeight: 700 }} />}
                            </Box>
                          </TableCell>
                          <TableCell sx={TD}>{scopeText(t, lang, r.request, r.homeCountry)}</TableCell>
                          <TableCell sx={TD}>{scopeText(t, lang, r.grant, r.homeCountry)}</TableCell>
                          <TableCell sx={TD}>{r.status === "approved" ? `${t("admin.localShort")} ${r.localOn ? t("admin.on") : t("admin.off")} · ${t("admin.intlShort")} ${r.internationalOn ? t("admin.on") : t("admin.off")}` : "—"}</TableCell>
                          <TableCell sx={{ ...TD, whiteSpace: "nowrap" }}>{(r.requestedUtc || r.decidedUtc) ? new Date(r.requestedUtc || r.decidedUtc).toLocaleDateString(lang) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
        </Box>
      )}
      {open && <BusinessDialog userId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </Box>
  );
}
